import {
  formatDelta,
  formatMetricValue,
  formatPeriodo,
  formatRelativeDays,
  listaEs,
  periodoAnterior,
} from './format.js'
import type { MetricValue } from './metrics.js'
import type { AdminOverview, MrrTrendPoint } from './overview.js'

/**
 * Titular en prosa del Overview del founder.
 *
 * Se arma con los mismos números que muestra el resto de la pantalla: nada
 * hardcodeado, nada que la pantalla no pueda probar abajo. Si un número no
 * está en el payload, la oración lo dice en lugar de inventarlo — es la misma
 * regla que "un KPI sin dato nunca muestra cero".
 */
export interface TitularFounder {
  /** Una oración: cómo viene el mes arriba y abajo. */
  veredicto: string
  /** El nivel y el crecimiento, con la base del delta a la vista. */
  loQueVaBien: string
  /** Lo que está trabado, con su tamaño. */
  loQueFrena: string
  /** La deuda de datos: qué no se puede leer y por qué. */
  loQueNoSeLee: string
}

/** A partir de acá el crecimiento se llama "fuerte" y no solo "en alza". */
export const UMBRAL_CRECIMIENTO_FUERTE_PCT = 10

/** Porcentaje del portfolio sin implementar o en retención que llama "trabado". */
export const UMBRAL_FRICCION_PORTFOLIO_PCT = 25

/** Mínimo de meses seguidos sin baja para que la racha valga mencionarse. */
export const MIN_MESES_RACHA = 3

export function titularFounder(
  payload: AdminOverview,
  now: Date = new Date(),
): TitularFounder {
  return {
    veredicto: veredictoDe(payload),
    loQueVaBien: loQueVaBienDe(payload),
    loQueFrena: loQueFrenaDe(payload),
    loQueNoSeLee: loQueNoSeLeeDe(payload, now),
  }
}

// ---------------------------------------------------------------------------
// Veredicto
// ---------------------------------------------------------------------------

function veredictoDe(p: AdminOverview): string {
  const mrr = metricaPorSlug(p, 'mrr')
  const friccion = friccionPortfolio(p)

  const arriba = (() => {
    if (mrr === null || mrr.valor === null) return 'El mes no tiene lectura de revenue'
    if (mrr.deltaPct === null) {
      return 'El mes tiene nivel de revenue pero todavía no tendencia'
    }
    if (mrr.deltaPct >= UMBRAL_CRECIMIENTO_FUERTE_PCT) return 'El mes viene fuerte arriba'
    if (mrr.deltaPct > 0) return 'El mes viene en alza arriba'
    if (mrr.deltaPct === 0) return 'El mes viene plano arriba'
    return 'El mes viene en baja arriba'
  })()

  const abajo = (() => {
    if (friccion.pct === null) {
      return 'abajo el portfolio todavía no tiene cuentas cargadas'
    }
    if (friccion.pct >= UMBRAL_FRICCION_PORTFOLIO_PCT) {
      return `abajo está trabado: el ${pct(friccion.pct)} del portfolio está sin implementar o en retención`
    }
    return 'abajo el portfolio está en orden'
  })()

  return `${arriba}, y ${abajo}.`
}

// ---------------------------------------------------------------------------
// Lo que va bien
// ---------------------------------------------------------------------------

function loQueVaBienDe(p: AdminOverview): string {
  const partes: string[] = []
  const mrr = metricaPorSlug(p, 'mrr')

  if (mrr !== null && mrr.valor !== null) {
    const nombreBase = periodoAnterior(p.periodo)
    const valorBase = mrrDelPeriodo(p.mrrTrend, nombreBase)
    const delta = formatDelta(mrr.deltaPct)

    if (delta !== null && nombreBase !== null && valorBase !== null) {
      // Regla de números derivables: el delta nunca va sin su base.
      partes.push(
        `MRR ${valor(mrr)}, ${delta} contra ${formatPeriodo(nombreBase)} (${formatMetricValue(valorBase, 'usd')})`,
      )
    } else if (delta !== null && nombreBase !== null) {
      partes.push(`MRR ${valor(mrr)}, ${delta} contra ${formatPeriodo(nombreBase)}`)
    } else {
      partes.push(
        `MRR ${valor(mrr)} en ${formatPeriodo(p.periodo)}, sin mes anterior con dato para comparar`,
      )
    }

    const racha = mesesSinBaja(p.mrrTrend)
    if (racha >= MIN_MESES_RACHA) {
      partes.push(`la serie de ${racha} meses no tiene un solo mes en baja`)
    }
  }

  const enVerde = p.heroMetrics.filter(
    (m) => m.slug !== 'mrr' && m.valor !== null && m.health === 'ok',
  )
  if (enVerde.length > 0) {
    const lista = listaEs(enVerde.map((m) => `${m.nombre} ${valor(m)}`))
    partes.push(`${lista} ${enVerde.length === 1 ? 'cumple' : 'cumplen'} su benchmark`)
  }

  if (partes.length === 0) {
    return 'Todavía no hay ninguna métrica en verde con dato cargado: no hay nada que anotar como logro del mes.'
  }
  return oracion(partes)
}

// ---------------------------------------------------------------------------
// Lo que frena
// ---------------------------------------------------------------------------

function loQueFrenaDe(p: AdminOverview): string {
  const partes: string[] = []
  const total = p.portfolio.total

  const pendientes = estado(p, 'pendiente_implementacion')
  if (pendientes !== null && pendientes.cantidad > 0) {
    partes.push(
      `${cuenta(pendientes.cantidad)} ${pendientes.cantidad === 1 ? 'cliente firmado sin implementar' : 'clientes firmados sin implementar'}, el ${pct(pendientes.pctPortfolio)} de las ${cuenta(total)} cuentas del portfolio`,
    )
  }

  const retencion = estado(p, 'retencion')
  if (retencion !== null && retencion.cantidad > 0) {
    partes.push(
      `${cuenta(retencion.cantidad)} ${retencion.cantidad === 1 ? 'cuenta pasó' : 'cuentas pasaron'} a retención`,
    )
  }

  const criticas = p.heroMetrics.filter(
    (m) => m.valor !== null && m.health === 'critical',
  )
  if (criticas.length > 0) {
    const lista = listaEs(
      criticas.map((m) =>
        m.benchmark === null
          ? `${m.nombre} en ${valor(m)}`
          : `${m.nombre} en ${valor(m)} contra un benchmark de ${m.benchmark}`,
      ),
    )
    partes.push(`fuera de umbral: ${lista}`)
  }

  const cuello = cuelloDelFunnel(p)
  if (cuello !== null) {
    partes.push(
      `en el funnel de ${formatPeriodo(p.funnel.periodo)}, de ${cuello.desde} a ${cuello.hasta} pasa el ${pct(cuello.conversionPct)}`,
    )
  }

  const alertas = p.alerts.filter((a) => a.severity === 'critical').length
  if (alertas > 0) {
    partes.push(
      `${cuenta(alertas)} ${alertas === 1 ? 'alerta crítica abierta' : 'alertas críticas abiertas'}`,
    )
  }

  if (partes.length === 0) {
    return 'Nada trabado: ni cuentas sin implementar ni métricas fuera de umbral.'
  }
  return oracion(partes)
}

// ---------------------------------------------------------------------------
// Lo que no se lee
// ---------------------------------------------------------------------------

function loQueNoSeLeeDe(p: AdminOverview, now: Date): string {
  const partes: string[] = []

  const sinDato = p.heroMetrics.filter((m) => m.valor === null)
  if (sinDato.length > 0) {
    const lista = listaEs(sinDato.map((m) => m.nombre))
    partes.push(
      sinDato.length === 1
        ? `Hay una métrica que hoy no podemos leer: ${lista}`
        : `Hay ${cuenta(sinDato.length)} métricas que hoy no podemos leer: ${lista}`,
    )
  }

  const vencidas = p.heroMetrics.filter(
    (m) => m.valor !== null && m.provenance.freshness === 'expired',
  )
  if (vencidas.length > 0) {
    const lista = listaEs(
      vencidas.map(
        (m) => `${m.nombre} (${formatRelativeDays(m.provenance.updatedAt, now)})`,
      ),
    )
    partes.push(
      vencidas.length === 1
        ? `la carga de ${lista} está vencida`
        : `las cargas de ${lista} están vencidas`,
    )
  }

  if (partes.length === 0) {
    return 'Todas las métricas del período tienen dato y carga vigente.'
  }
  return oracion(partes)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function metricaPorSlug(p: AdminOverview, slug: string): MetricValue | null {
  return p.heroMetrics.find((m) => m.slug === slug) ?? null
}

function estado(
  p: AdminOverview,
  nombre: string,
): { cantidad: number; pctPortfolio: number } | null {
  const fila = p.portfolio.porEstado.find((e) => e.estado === nombre)
  return fila === undefined
    ? null
    : { cantidad: fila.cantidad, pctPortfolio: fila.pctPortfolio }
}

/** Porcentaje del portfolio sin implementar o en retención. */
function friccionPortfolio(p: AdminOverview): { pct: number | null } {
  if (p.portfolio.total <= 0) return { pct: null }
  const trabados = p.portfolio.porEstado
    .filter(
      (e) => e.estado === 'pendiente_implementacion' || e.estado === 'retencion',
    )
    .reduce((acc, e) => acc + e.cantidad, 0)
  return { pct: (trabados / p.portfolio.total) * 100 }
}

function mrrDelPeriodo(
  trend: readonly MrrTrendPoint[],
  periodo: string | null,
): number | null {
  if (periodo === null) return null
  return trend.find((t) => t.periodo === periodo)?.mrr ?? null
}

/**
 * Meses del final de la serie sin una sola baja, contando el punto inicial de
 * la racha. Devuelve 0 si la serie tiene menos de dos puntos.
 */
function mesesSinBaja(trend: readonly MrrTrendPoint[]): number {
  if (trend.length < 2) return 0
  let racha = 1
  for (let i = trend.length - 1; i > 0; i -= 1) {
    const actual = trend[i]
    const previo = trend[i - 1]
    if (actual === undefined || previo === undefined) break
    if (actual.mrr < previo.mrr) break
    racha += 1
  }
  return racha === 1 ? 0 : racha
}

/** Etapa con la peor conversión del funnel, con el nombre de su etapa previa. */
function cuelloDelFunnel(
  p: AdminOverview,
): { desde: string; hasta: string; conversionPct: number } | null {
  let peor: { desde: string; hasta: string; conversionPct: number } | null = null
  for (let i = 1; i < p.funnel.etapas.length; i += 1) {
    const etapa = p.funnel.etapas[i]
    const previa = p.funnel.etapas[i - 1]
    if (etapa === undefined || previa === undefined) continue
    if (etapa.conversionPct === null) continue
    if (peor === null || etapa.conversionPct < peor.conversionPct) {
      peor = {
        desde: previa.nombre,
        hasta: etapa.nombre,
        conversionPct: etapa.conversionPct,
      }
    }
  }
  return peor
}

function valor(m: MetricValue): string {
  return formatMetricValue(m.valor, m.unidad)
}

function pct(n: number): string {
  return formatMetricValue(n, 'percent')
}

function cuenta(n: number): string {
  return formatMetricValue(n, 'count')
}

/** Une las partes en oraciones cerradas, cada una con mayúscula inicial. */
function oracion(partes: readonly string[]): string {
  return `${partes.map(mayuscula).join('. ')}.`
}

function mayuscula(texto: string): string {
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}`
}
